/**
 * One way for the theme to send an analytics event, and one place that knows
 * gtag might not be there yet.
 *
 *   EngineEvents.send('generate_lead', { form_location: 'contact_page' });
 *   EngineEvents.once('key', function () { ... });
 *
 * Why the queue
 * -------------
 * gtag is not loaded by this theme. It arrives from the Google & YouTube
 * channel after the page is interactive, which means a fast submit can happen
 * before window.gtag exists. The obvious fallback - pushing onto dataLayer -
 * does nothing here: dataLayer only becomes a GA4 event when Tag Manager is
 * reading it, and this store runs gtag.js directly. An event pushed there is
 * simply lost.
 *
 * So events queue and flush when gtag appears. A lead we paid for and did not
 * count is worse than a slow event.
 *
 * The queue also flushes when the page is hidden and on pagehide, because the
 * commonest moment to lose one is a visitor who submits and immediately
 * switches tab. Nothing here throws: analytics must never break a form.
 */
(function () {
  'use strict';

  var queue = [];
  var timer = null;
  var waitingSince = Date.now();

  /* Long enough for a slow third-party loader on a slow connection, short
     enough that a page which will never load gtag stops polling. */
  var CEILING_MS = 30000;
  var POLL_MS = 200;

  function ready() {
    return typeof window.gtag === 'function';
  }

  function stop() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function flush() {
    if (!ready()) return;
    while (queue.length) {
      var item = queue.shift();
      try {
        window.gtag('event', item.name, item.params);
      } catch (e) {
        /* a refused event is not worth breaking a page over */
      }
    }
    stop();
  }

  function watch() {
    if (timer) return;
    timer = setInterval(function () {
      if (ready()) {
        flush();
        return;
      }
      if (Date.now() - waitingSince > CEILING_MS) stop();
    }, POLL_MS);
  }

  function send(name, params) {
    if (!name) return;
    var clean = {};
    var src = params || {};
    for (var k in src) {
      if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
      var v = src[k];
      /* undefined, null and empty string are not parameters, they are noise */
      if (v === undefined || v === null || v === '') continue;
      clean[k] = v;
    }
    queue.push({ name: name, params: clean });
    waitingSince = Date.now();
    if (ready()) flush();
    else watch();
  }

  /*
    Fire once per key, for an event that renders from the server and would
    otherwise repeat whenever the page it rendered on is shown again.

    If sessionStorage is unavailable - a private window, blocked site data -
    the callback still runs. Counting a lead twice is recoverable; never
    counting it is not.
  */
  function once(key, fn) {
    var seen = false;
    try {
      seen = window.sessionStorage.getItem(key) !== null;
      if (!seen) window.sessionStorage.setItem(key, '1');
    } catch (e) {
      seen = false;
    }
    if (seen) return false;
    fn();
    return true;
  }

  /*
    How this navigation arrived. A success page reached by reload or by the
    back button is the same submission being looked at again, not a new one.
  */
  function navigationType() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type;
    } catch (e) {}
    return 'navigate';
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);

  window.EngineEvents = {
    send: send,
    once: once,
    navigationType: navigationType,
    /* for the probe: how many events are still waiting */
    pending: function () { return queue.length; }
  };
})();
